// src/features/reit-info/components/DividendHistoryChart.tsx
//
// Component Island (Island 2 — NEW & CRUCIAL): the REIT's dividend payout
// track record. FMP returns individual (monthly/quarterly) payouts newest-
// first; we aggregate the split-adjusted amounts by calendar year to render a
// clean dividend-growth bar chart — the single most important consistency
// signal for a Real-Estate Investment Trust.
//
// Bars are coloured emerald and the most recent (possibly partial) year is
// dimmed so a mid-year snapshot isn't mistaken for a payout cut.

import { useEffect, useMemo, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { fetchReitDividends, type ReitDividend } from '../api';

interface YearBar {
    year: string;
    total: number;
}

export const DividendHistoryChart = ({ symbol }: { symbol: string }) => {
    const [dividends, setDividends] = useState<ReitDividend[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchReitDividends(symbol, 200)
            .then((res) => {
                if (!cancelled) setDividends(res);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) {
                    setError('Dividend history unavailable.');
                    setDividends([]);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    // Aggregate split-adjusted payouts by calendar year (oldest -> newest).
    const yearBars = useMemo<YearBar[]>(() => {
        const totals = new Map<string, number>();
        for (const d of dividends) {
            if (!d.date || d.dividend == null) continue;
            const year = d.date.slice(0, 4);
            if (!/^\d{4}$/.test(year)) continue;
            totals.set(year, (totals.get(year) ?? 0) + d.dividend);
        }
        return Array.from(totals.entries())
            .map(([year, total]) => ({ year, total: Number(total.toFixed(4)) }))
            .sort((a, b) => a.year.localeCompare(b.year));
    }, [dividends]);

    const currentYear = String(new Date().getFullYear());

    // The trailing frequency label (e.g. "Monthly") for the subtitle.
    const frequency = dividends.find((d) => d.frequency)?.frequency;

    return (
        <div className="surface-card flex flex-col h-full">
            <h2 className="section-title mb-1">Dividend History</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                Split-adjusted payouts per calendar year
                {frequency ? ` • ${frequency} payer` : ''}
            </p>

            <div className="relative h-[320px] w-full">
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center text-blue-500 font-medium animate-pulse text-sm">
                        Loading dividend history…
                    </div>
                )}

                {!isLoading && error && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        {error}
                    </div>
                )}

                {!isLoading && !error && yearBars.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        No dividend history available.
                    </div>
                )}

                {!isLoading && !error && yearBars.length > 0 && (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={yearBars}
                            margin={{ left: -10, bottom: 0, top: 10, right: 10 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#334155"
                                opacity={0.3}
                            />
                            <XAxis
                                dataKey="year"
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                minTickGap={8}
                            />
                            <YAxis
                                tickFormatter={(val: number) => `$${val.toFixed(2)}`}
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                width={56}
                            />
                            <Tooltip
                                cursor={{ fill: '#33415533' }}
                                contentStyle={{
                                    backgroundColor: '#0f172a',
                                    border: '1px solid #334155',
                                    borderRadius: '8px',
                                    color: '#e2e8f0',
                                }}
                                labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                                itemStyle={{ color: '#e2e8f0' }}
                                formatter={(value: number) => [
                                    `$${Number(value).toFixed(4)}`,
                                    'Dividends / share',
                                ]}
                            />
                            <Bar
                                dataKey="total"
                                name="Dividends / share"
                                radius={[4, 4, 0, 0]}
                                isAnimationActive={false}
                            >
                                {yearBars.map((b) => (
                                    <Cell
                                        key={b.year}
                                        fill={b.year === currentYear ? '#6ee7b7' : '#10b981'}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default DividendHistoryChart;
